import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { Card, CardHeader, CardBody, CardFooter, CardTitle, Row, Spinner, Button } from 'reactstrap';
import { useQuery, useMutation } from '@tanstack/react-query';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import ReactTable from 'components/ReactTable/ReactTable';
import { formatDate } from 'helpers/formatter';
import {
  listUserSavedReturns as _listUserSavedReturns,
  deleteUserSavedReturns as _deleteUserSavedReturns,
} from 'services/user-saved-returns';

const isEmptyOrAllFalse = (obj) => {
  return Object.keys(obj).length === 0 || Object.values(obj).every((value) => !value);
};

const getTrueKeys = (obj) => {
  return Object.entries(obj)
    .filter(([_, value]) => value === true)
    .map(([key]) => key);
};

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = useRef();
  const resolvedRef = ref || defaultRef;

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return (
    <>
      <input type="checkbox" ref={resolvedRef} {...rest} />
    </>
  );
});

let selectedNames = {};

function DeleteButton({ notify, confirm, onSuccess }) {
  const [visible, setVisible] = useState(false);

  const { mutateAsync: deleteUserSavedReturns, isPending: isDeletingUserSavedReturns } = useMutation({
    mutationFn: _deleteUserSavedReturns,
    onSuccess: () => {
      notify.success({ message: <div>Deleted successfully</div> });
      onSuccess();
    },
    onError: (error) => {
      notify.danger({ message: <div>{String(error)}</div> });
    },
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(!isEmptyOrAllFalse(selectedNames));
    }, 500);

    return () => clearInterval(interval);
  }, []);

  if (!visible) return null;

  return (
    <Button
      className="btn tw-m-0"
      size="sm"
      color="danger"
      onClick={() => {
        const assetNames = _.orderBy(getTrueKeys(selectedNames), null, 'asc');

        confirm({
          content: (
            <div>
              <div>This will delete the following uploaded assets. Are you sure you would like to continue?</div>
              <ul>
                {assetNames.map((name) => {
                  return <li key={name}>{name}</li>;
                })}
              </ul>
            </div>
          ),
          callback: async (result) => {
            if (result != 'confirmed') return;
            await deleteUserSavedReturns(assetNames);
          },
        });
      }}
    >
      Delete
    </Button>
  );
}

function ImportDataTable({ notify, confirm }) {
  const [page, setPage] = useState(0);

  const {
    data: userSavedReturns,
    isLoading: isLoadingUserSavedReturns,
    isError: isUserSavedReturnsError,
    error: userSavedReturnsError,
    refetch: refetchUserSavedReturns,
  } = useQuery({
    queryKey: ['user-saves'],
    queryFn: () => _listUserSavedReturns(),
    refetchInterval: 500,
  });

  useEffect(() => {
    selectedNames = {};
  }, [userSavedReturns]);

  if (isLoadingUserSavedReturns || !userSavedReturns) {
    return (
      <div className="tw-text-center mb-5">
        <Spinner>Loading...</Spinner>
      </div>
    );
  }

  const data = userSavedReturns.map((item) => ({ name: item }));

  return (
    <Card className="tw-max-w-2xl tw-mx-auto">
      <CardHeader>
        <CardTitle tag="h4" className="tw-mb-0 tw-flex tw-justify-between">
          <span>Uploaded Assets</span>
          <DeleteButton
            notify={notify}
            confirm={confirm}
            onSuccess={() => {
              refetchUserSavedReturns();
            }}
          />
        </CardTitle>
      </CardHeader>
      <CardBody>
        <ReactTable
          data={data}
          initialPage={page}
          initialPageSize={10}
          navigatePage={setPage}
          onRowSelect={(row, pages) => {
            row.toggleRowSelected(!row.isSelected);
            selectedNames[row.original.name] = !row.isSelected;
          }}
          rowSelectable
          columns={[
            {
              Header: 'Name',
              accessor: 'name',
              disableSortBy: false,
              disableFilters: false,
              Cell: ({ value: initialValue, row, column }) => {
                const { name } = row.original;
                const toggleRowSelectedProps = row.getToggleRowSelectedProps();

                return (
                  <div className="tw-text-left tw-cursor-pointer">
                    <IndeterminateCheckbox {...toggleRowSelectedProps} />
                    <span className="ml-2">{name}</span>
                  </div>
                );
              },
            },
          ]}
          className="-striped -highlight primary-pagination"
        />
      </CardBody>
    </Card>
  );
}

export default ImportDataTable;
